// auth.api.ts
import axios from 'axios';
import i18n from '@app/i18n';
import { BaseResponse } from '@app/entities/BaseResponse';
import { apiRequest } from './apiUtils';
import { ApiError } from './ApiError';
import { fetchUserIp, storeUserIp } from '@app/utils/ipUtils';

// -------- Types (kept from your current file) --------
export interface AuthData {
  email: string;
  password: string;
}

export interface SignUpRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface SecurityCodePayload {
  code: string;
}

export interface NewPasswordData {
  newPassword: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SwipeRequest {
  cardData: string;
}

export interface SsoResponse {
  access_token: string;
  user: any; // keep UserModel if you want strict typing
}

// -------- Base URL --------
const BASE_URL = 'auth';

// Small helper to translate a few legacy code paths to friendly i18n messages
const translateLoginErrors = (err: any): never => {
  const status = err?.options?.status ?? err?.status;
  const code = err?.options?.code ?? err?.code;

  if (status === 401 || status === 404) {
    switch (code) {
      case '100003':
      case '400002':
        throw new ApiError(i18n.t('login.loginError'));
      case '500047':
      case '500048':
      case '500112':
        throw new ApiError(i18n.t('login.accessDeniedError'));
    }
  }

  // Not a mapped/translated case -> rethrow original
  throw err;
};

// -------- APIs (MD3 style with apiRequest) --------

export const login = async (
  payload: LoginRequest
): Promise<BaseResponse<SsoResponse> | undefined> => {
  try {
    const response = await apiRequest<BaseResponse<SsoResponse>>(
      'POST',
      `${BASE_URL}/login`,
      payload
    );
    
    // Fetch and store user's IP address on successful login
    if (response) {
      try {
        const userIp = await fetchUserIp();
        if (userIp) {
          storeUserIp(userIp);
          console.log('[Auth] User IP fetched and stored on login:', userIp);
        } else {
          console.warn('[Auth] Failed to fetch user IP on login');
        }
      } catch (ipError) {
        console.warn('[Auth] Error during IP detection on login:', ipError);
        // Don't block login if IP detection fails
      }
    }
    
    return response;
  } catch (err) {
    translateLoginErrors(err);
  }
};

export const swipeLogin = async (
  payload: SwipeRequest
): Promise<BaseResponse<SsoResponse> | undefined> => {
  try {
    const response = await apiRequest<BaseResponse<SsoResponse>>(
      'POST',
      `${BASE_URL}/swipe-access-card`,
      payload
    );
    
    // Fetch and store user's IP address on successful login
    if (response) {
      try {
        const userIp = await fetchUserIp();
        if (userIp) {
          storeUserIp(userIp);
          console.log('[Auth] User IP fetched and stored on swipe login:', userIp);
        }
      } catch (ipError) {
        console.warn('[Auth] Error during IP detection on swipe login:', ipError);
        // Don't block login if IP detection fails
      }
    }
    
    return response;
  } catch (err) {
    translateLoginErrors(err);
  }
};

/**
 * IMPORTANT: Use a raw axios instance here (no interceptors) to avoid
 * recursion when commonHttpApi’s request interceptor tries to refresh again.
 */
const rawAxios = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'x-api-key': process.env.REACT_APP_APPLICATION_API_KEY ?? '',
  },
});

export const refreshToken = async (): Promise<BaseResponse<SsoResponse> | undefined> => {
  try {
    const { data } = await rawAxios.get<BaseResponse<SsoResponse>>(
      `${BASE_URL}/refresh-token`
    );
    return data;
  } catch (err) {
    // Intentionally don’t translate here; caller/axios interceptor can decide logout flow.
    return undefined;
  }
};

// Mock-style endpoints kept for completeness (if still used elsewhere)
export const signUp = (signUpData: SignUpRequest): Promise<undefined> =>
  apiRequest<undefined>('POST', `signUp`, signUpData);

export const resetPassword = (resetPasswordPayload: ResetPasswordRequest): Promise<undefined> =>
  apiRequest<undefined>('POST', `forgotPassword`, resetPasswordPayload);

export const verifySecurityCode = (securityCodePayload: SecurityCodePayload): Promise<undefined> =>
  apiRequest<undefined>('POST', `verifySecurityCode`, securityCodePayload);

export const setNewPassword = (newPasswordData: NewPasswordData): Promise<undefined> =>
  apiRequest<undefined>('POST', `setNewPassword`, newPasswordData);

export const logout = async (
  autoLogout: boolean = true
): Promise<BaseResponse<SsoResponse> | undefined> => {
  try {
    const result = await apiRequest<BaseResponse<SsoResponse>>(
      'POST',
      `${BASE_URL}/logout`
    );
    return result;
  } catch (err) {

    return undefined;
  }
};